import { useEffect, useState } from 'react';
import { AppPageHeader, Group, InfiniteTable } from '@/components/_common';
import { ListInstallmentsQuery } from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Button } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import moment from 'moment';
import { useIntl } from 'react-intl';

import StatusTag from '@/components/_common/StatusTag';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import EyeIcon from '@/components/_common/EyeIcon';
import BillToPayMigrationConsultDetailsModal from '../BillToPayMigrationConsultDetailsModal';
import BillToPayMigrationConsultExtraFilters from '../BillToPayMigrationConsultExtraFilters';
import { useFinancialConsultationPayableList } from '@/core/domain/financial/Consultation/consultation.hooks';
import {
  FinancialConsultInstallmentSituation,
  FinancialConsultInstallmentSituationType,
  FinancialConsultPayable,
  FinancialConsultPayableListRequest,
} from '@/core/domain/financial/Consultation/consultation.types';
import BillToPayMigrationConsultBind from '../BillToPayMigrationConsultBind';
import MigrationInstallmentBinded from '../../MigrationInstallmentBinded';

export default function BillToPayMigrationConsultList() {
  const intl = useIntl();
  const [installmentPayableSelected, setInstallmentPayableSelected] =
    useState<FinancialConsultPayable>();

  const [queryParams, setQueryParams] = useState<ListInstallmentsQuery>({
    order: 'DESC',
    ...convertParams<FinancialConsultPayableListRequest>({
      search: 'string',
      code: 'string',
      companyId: 'number',
      creditor: 'string',
      description: 'string',
      documentType: 'number',
      dueDate: 'string',
      payDate: 'string',
      emissionDate: 'string',
      situation: 'string',
      installmentValue: 'number',
      documentNumber: 'string',
      order: 'string',
      observation: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const installmentConsultList =
    useFinancialConsultationPayableList(queryDebounce);

  const installments = flatInfiniteData(installmentConsultList.data);

  const columns: ColumnsType<FinancialConsultPayable> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({ id: 'releaseBillToPay.list.header.code' }),
      width: 140,
      key: 'code',
      render: (_, row) => `${row.code}-${row.installmentNumber}`,
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.companies',
      }),
      width: 220,
      key: 'company',
      ellipsis: true,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentNumber',
      }),
      width: 220,
      key: 'documentNumber',
      ellipsis: true,
    },
    {
      dataIndex: 'creditor',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
      width: 220,
      key: 'creditor',
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.description',
      }),
      width: COLUMNS_WIDTH.action2,
      key: 'description',
    },
    {
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentType',
      }),
      width: COLUMNS_WIDTH.price,
      key: 'documentType',
    },
    {
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: 'emissionDate',
    },
    {
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: 'dueDate',
    },
    {
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: 'payDate',
    },
    {
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.situation',
      }),
      width: COLUMNS_WIDTH.price,
      key: 'situation',
      render: (situation: FinancialConsultInstallmentSituation) => {
        const situationType =
          FinancialConsultInstallmentSituationType[situation];
        return (
          <StatusTag label={situationType.label} color={situationType.color} />
        );
      },
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue + (record.discountIncrease ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: 'value',
    },
    {
      key: 'actions',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      render: (_, item) => (
        <Group position="right" gap={10}>
          <MigrationInstallmentBinded
            accountId={item.boundAccountConsultation.mainId}
            installmentId={item.boundAccountConsultation.mainInstallmentId}
            type="payable"
          />

          <EyeIcon
            iconProps={{ onClick: () => setInstallmentPayableSelected(item) }}
          />

          <BillToPayMigrationConsultBind
            migrationId={item.accountConsultationId}
          />
        </Group>
      ),
      ellipsis: true,
      width: 80,
      fixed: 'right',
      align: 'center',
    },
  ];

  return (
    <>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <BillToPayMigrationConsultExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<FinancialConsultPayable>
        columns={columns}
        dataSource={installments}
        loading={installmentConsultList.isFetching}
        rowKey={(row) => row.id}
        hasNextPage={installmentConsultList.hasNextPage}
        fetchNextPage={installmentConsultList.fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <BillToPayMigrationConsultDetailsModal
        open={!!installmentPayableSelected}
        onCancel={() => setInstallmentPayableSelected(undefined)}
        installmentData={installmentPayableSelected}
      />
    </>
  );
}
